import { FC, useContext, useEffect } from 'react';

import { useApolloClient } from '@apollo/client';
import {
  Button,
  Modal,
  NumberInput,
  Tabs,
  Toggle,
  DateRangePicker,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { getRandomNumber } from 'utils';

import { GET_CACHED_CASHBACK, ICashbackInput } from '../schema';
import { TCashbackRule } from '../types';
import { useCashbackRuleMutations } from '../useCashbackRuleMutations';
import s from './CashbackManager.module.scss';

interface ICashbackManagerProps {
  cashbackId: number | null;
  onSuccess: () => void;
  onClose: () => void;
}

enum ECashbackType {
  PERCENT,
  SUM,
}
interface IFormValues {
  amount: number | '';
  value: number | '';
  type: ECashbackType;
  is_active: boolean;
  infinite: boolean;
  startDate: string | null;
  endDate: string | null;
}

const DEFAULT_VALUES: IFormValues = {
  value: '',
  amount: '',
  is_active: true,
  infinite: true,
  type: ECashbackType.PERCENT,
  startDate: null,
  endDate: null,
};

export const CashbackManager: FC<ICashbackManagerProps> = ({
  cashbackId,
  onSuccess,
  onClose,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    setError,
    setValue,
    formState: { isDirty, isSubmitting },
  } = useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });

  const value = watch('value');
  const amount = watch('amount');
  const type = watch('type');
  const infinite = watch('infinite');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const hasValueAndAmount = Boolean(value) && Boolean(amount);

  const apolloClient = useApolloClient();
  const {
    createCashback,
    updateCashback,
    deleteCashback,
    isDeleting,
    updateError,
    createError,
  } = useCashbackRuleMutations({
    onCreate: onSuccess,
    onDelete: onSuccess,
    onUpdate: onSuccess,
  });

  useEffect(() => {
    const error = createError ?? updateError;

    if (error?.message === 'deposit cashback threshold is taken') {
      setError('amount', { message: t('errors.cashback.sum.exist') });
    }
  }, [createError, updateError, t]);

  useEffect(() => {
    if (value) {
      trigger('value').catch(console.error);
    }
  }, [type]);

  useEffect(() => {
    if (cashbackId) {
      const cachedRule = apolloClient.cache.readQuery<TCashbackRule | null>({
        query: GET_CACHED_CASHBACK,
        id: apolloClient.cache.identify({
          id: cashbackId,
          __typename: 'DepositCashback',
        }),
      });

      if (!cachedRule) return;

      const starts = dayjs(cachedRule.starts_at);
      const ends = dayjs(cachedRule.expires_at);

      const newValues: IFormValues = {
        amount: cachedRule.amount,
        value: cachedRule.value,
        type: cachedRule.is_percent ? ECashbackType.PERCENT : ECashbackType.SUM,
        is_active: cachedRule.is_active,
        infinite: !cachedRule.expires_at,
        startDate: null,
        endDate: null,
      };

      if (starts.isValid()) {
        newValues.startDate = starts.format();
      }
      if (ends.isValid()) {
        newValues.endDate = ends.format();
      }

      reset(newValues);

      return;
    }

    reset(DEFAULT_VALUES);
  }, [cashbackId]);

  return (
    <Modal onClose={onClose}>
      <form
        onSubmit={handleSubmit((values) => {
          if (values.amount === '' || values.value === '') return;

          const input: ICashbackInput = {
            amount: values.amount,
            value: values.value,
            is_active: values.is_active,
            is_percent: values.type === ECashbackType.PERCENT,
          };

          if (!values.infinite) {
            if (values.startDate) {
              input.starts_at = dayjs(values.startDate).format(
                'YYYY-MM-DD 00:00:00'
              );
            }
            if (values.endDate) {
              input.expires_at = dayjs(values.endDate).format(
                'YYYY-MM-DD 23:59:59'
              );
            }
          }

          if (cashbackId) {
            return updateCashback({ variables: { id: cashbackId, input } });
          }

          return createCashback({ variables: { input } });
        })}
        className={s.form}
      >
        <span className={s.header}>
          {t(cashbackId ? 'cashback.edit' : 'cashback.new')}
        </span>
        <div className={s.group}>
          <span className={s.title}>{t('cashback.setting')}</span>
          <div className={s.fields}>
            <Controller
              render={({
                field: { name, ...field },
                fieldState: { error },
              }) => (
                <NumberInput
                  {...field}
                  title={t('cashback.fill.sum')}
                  name={`${name}-${getRandomNumber()}`}
                  error={error?.message}
                  placeholder="1000"
                />
              )}
              name="amount"
              control={control}
              rules={{
                required: t('errors.required'),
                min: {
                  value: 1,
                  message: t('errors.min', 1),
                },
                max: {
                  value: 16000000,
                  message: t('errors.max', '16 000 000'),
                },
              }}
            />
            <div className={s.row}>
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Tabs
                    value={value}
                    onChange={onChange}
                    options={[
                      {
                        iconName: 'PercentIcon',
                        value: ECashbackType.PERCENT,
                      },
                      {
                        iconName: 'MoneyIcon',
                        value: ECashbackType.SUM,
                      },
                    ]}
                    className={s.type}
                  />
                )}
                name="type"
                control={control}
              />
              <Controller
                render={({
                  field: { name, ...field },
                  fieldState: { error },
                }) => (
                  <NumberInput
                    {...field}
                    title={t('cashback.size')}
                    name={`${name}-${getRandomNumber()}`}
                    error={error?.message}
                    placeholder="5"
                  />
                )}
                name="value"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value === '') return t('errors.required');

                    if (type === ECashbackType.PERCENT) {
                      if (value < 1) return t('errors.min', '1%');
                      if (value > 100) return t('errors.max', '100%');
                    }

                    if (type === ECashbackType.SUM) {
                      if (value < 1) return t('errors.min', 1);
                      if (value > 16000000)
                        return t('errors.max', '16 000 000');
                    }
                  },
                }}
              />
            </div>
          </div>
          {hasValueAndAmount && (
            <span className={s.description}>
              {t(
                'cashback.amount.help',
                amount,
                currency.sign,
                value,
                type === ECashbackType.PERCENT ? '%' : currency.sign
              )}
            </span>
          )}
        </div>
        <div className={s.group}>
          <span className={s.title}>{t('cashback.duration')}</span>
          <div className={s.fields}>
            <div className={cn(s.date_block, infinite && s.disabled)}>
              <DateRangePicker
                startDate={startDate}
                setStartDate={(value) =>
                  setValue('startDate', value, { shouldDirty: true })
                }
                endDate={endDate}
                setEndDate={(value) =>
                  setValue('endDate', value, { shouldDirty: true })
                }
                firstValidDate={dayjs().format()}
                withTime={false}
                hasPreselectedRange={false}
                fullWidth={true}
                lang={lang}
                isMobile={true}
              />
            </div>
            <Controller
              render={({ field: { value, onChange } }) => (
                <Toggle
                  value={value}
                  onChange={(e: boolean) => {
                    onChange(e);
                  }}
                  label={t('cashback.duration.infinite')}
                  iconName="PercentIcon"
                />
              )}
              name="infinite"
              control={control}
            />
          </div>
        </div>
        <div className={s.group}>
          <span className={s.title}>{t('state')}</span>
          <Controller
            render={({ field: { value, onChange } }) => (
              <Toggle
                value={value}
                onChange={onChange}
                label={t('on')}
                iconName="ShutdownIcon"
              />
            )}
            name="is_active"
            control={control}
          />
        </div>
        <div className={s.controls}>
          <Button
            type="submit"
            fullWidth
            className={s['submit-button']}
            disabled={!isDirty || isDeleting}
            isLoading={isSubmitting}
          >
            {t(cashbackId ? 'save' : 'add')}
          </Button>
          {cashbackId && (
            <Button
              variant="secondary"
              fullWidth
              color="red"
              disabled={isSubmitting || isDeleting}
              onClick={() => deleteCashback({ variables: { id: cashbackId } })}
            >
              {t('delete')}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};
